import type {RefObject} from 'react';
import {useLayoutEffect, useRef} from 'react';

/** Where the visible area ends across a row when only one layout counts: 0.5 cuts it through the middle. */
const PEEK_ROW_FRACTION = 0.5;
/** A row only reads as "cut" when at least this share of it is visible and at least this share is hidden. */
const MIN_CUT_ROW_FRACTION = 0.25;

interface PeekRowBounds {
  top: number;
  bottom: number;
}

interface PeekingScrollHeightOptions {
  scrollContainerRef: RefObject<HTMLElement | null>;
  peekItemSelector: string;
  /** Changing it re-fits the height. */
  remeasureKey: string;
  /**
   * How far every item would move in the container's other layout (e.g. +N px when a section N px tall is shown in it but
   * not now). When non-zero the height is the same in both layouts: one that cuts a row in both where one fits, otherwise
   * the one that cuts a row in the more compact layout.
   */
  getAlternateLayoutShift?: () => number;
}

/**
 * Sizes a scroll container so its visible area ends partway through a row of `peekItemSelector` items, leaving that
 * row visibly cut off as a cue that the content scrolls. Only acts when the content overflows the space the layout
 * gives the container; re-fits on window resize, when the content resizes or gains/loses sections, and whenever
 * `remeasureKey` changes.
 */
export function usePeekingScrollHeight({scrollContainerRef, peekItemSelector, remeasureKey, getAlternateLayoutShift}: PeekingScrollHeightOptions): void {
  const getAlternateLayoutShiftRef = useRef(getAlternateLayoutShift);
  getAlternateLayoutShiftRef.current = getAlternateLayoutShift;

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const fitToPeekingRow = (): void => {
      container.style.height = '';
      const availableHeight = container.clientHeight;
      if (container.scrollHeight <= availableHeight) {
        return;
      }
      const rows = getVisibleItemRows(container, peekItemSelector);
      const alternateLayoutShift = getAlternateLayoutShiftRef.current?.() ?? 0;
      const peekHeight =
        getPeekHeightForBothLayouts(rows, availableHeight, alternateLayoutShift) ?? getPeekHeight(getRowsInCompactLayout(rows, alternateLayoutShift), availableHeight);
      if (peekHeight !== null) {
        container.style.height = `${peekHeight}px`;
      }
    };

    const contentResizeObserver = new ResizeObserver(fitToPeekingRow);
    const observeContent = (): void => {
      Array.from(container.children).forEach((child) => {
        contentResizeObserver.observe(child);
      });
    };
    // Sections can mount after the container does (the brand kit renders nothing until brands load), so watch for new children too.
    const contentMutationObserver = new MutationObserver((): void => {
      observeContent();
      fitToPeekingRow();
    });

    fitToPeekingRow();
    observeContent();
    contentMutationObserver.observe(container, {childList: true});
    window.addEventListener('resize', fitToPeekingRow);

    return (): void => {
      contentResizeObserver.disconnect();
      contentMutationObserver.disconnect();
      window.removeEventListener('resize', fitToPeekingRow);
      container.style.height = '';
    };
  }, [remeasureKey]);
}

/**
 * The rows as they sit in whichever layout places them higher. When no single height cuts a row in both layouts, both
 * still get the height that cuts one in this layout, so switching layouts never changes the height.
 */
function getRowsInCompactLayout(rows: PeekRowBounds[], alternateLayoutShift: number): PeekRowBounds[] {
  const shift = Math.min(alternateLayoutShift, 0);
  return rows.map((row) => {
    return {top: row.top + shift, bottom: row.bottom + shift};
  });
}

/** The cut point of the last row that fits in `availableHeight`, or null if none does. */
function getPeekHeight(rows: PeekRowBounds[], availableHeight: number): number | null {
  let peekHeight: number | null = null;
  for (const row of rows) {
    const cutPoint = row.top + (row.bottom - row.top) * PEEK_ROW_FRACTION;
    if (cutPoint <= availableHeight) {
      peekHeight = cutPoint;
    }
  }
  return peekHeight;
}

/**
 * The tallest height (within `availableHeight`) that cuts a row now and also cuts a row once every item moves by
 * `alternateLayoutShift`, or null when no height does or there is no other layout.
 */
function getPeekHeightForBothLayouts(rows: PeekRowBounds[], availableHeight: number, alternateLayoutShift: number): number | null {
  if (alternateLayoutShift === 0) {
    return null;
  }
  let peekHeight: number | null = null;
  for (const row of rows) {
    const currentCutRange = getCutRange(row, 0);
    for (const alternateRow of rows) {
      const alternateCutRange = getCutRange(alternateRow, alternateLayoutShift);
      const sharedTop = Math.max(currentCutRange.top, alternateCutRange.top);
      const sharedBottom = Math.min(currentCutRange.bottom, alternateCutRange.bottom);
      const cutPoint = (sharedTop + sharedBottom) / 2;
      if (sharedTop <= sharedBottom && cutPoint <= availableHeight && (peekHeight === null || cutPoint > peekHeight)) {
        peekHeight = cutPoint;
      }
    }
  }
  return peekHeight;
}

/** The band of heights that leaves `row`, moved by `shift`, visibly cut. */
function getCutRange(row: PeekRowBounds, shift: number): PeekRowBounds {
  const minimumCut = (row.bottom - row.top) * MIN_CUT_ROW_FRACTION;
  return {top: row.top + shift + minimumCut, bottom: row.bottom + shift - minimumCut};
}

/** Item bounds in the container's scroll coordinates, skipping items clipped away by a collapsed ancestor. */
function getVisibleItemRows(container: HTMLElement, peekItemSelector: string): PeekRowBounds[] {
  const containerTop = container.getBoundingClientRect().top - container.scrollTop;
  return Array.from(container.querySelectorAll<HTMLElement>(peekItemSelector))
    .filter((item) => {
      return !isClippedBetween(item, container);
    })
    .map((item) => {
      const itemRect = item.getBoundingClientRect();
      return {top: itemRect.top - containerTop, bottom: itemRect.bottom - containerTop};
    });
}

function isClippedBetween(item: HTMLElement, container: HTMLElement): boolean {
  const itemBottom = item.getBoundingClientRect().bottom;
  for (let ancestor = item.parentElement; ancestor && ancestor !== container; ancestor = ancestor.parentElement) {
    const isClippingAncestor = window.getComputedStyle(ancestor).overflowY !== 'visible';
    if (isClippingAncestor && itemBottom > ancestor.getBoundingClientRect().bottom) {
      return true;
    }
  }
  return false;
}
